// src/ConsumerApp/api/addresses/deliveryAddress.hooks.js

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DeliveryAddressService } from './deliveryAddress.service';

export const useGetDeliveryAddresses = (customerId) => {
  return useQuery({
    queryKey: ['deliveryAddresses', customerId],
    queryFn: async () => {
      const response = await DeliveryAddressService.getDeliveryAddresses(customerId);
      const list = response?.data || response?.addresses || response;
      return Array.isArray(list) ? list : [];
    },
    enabled: typeof customerId === 'string' || typeof customerId === 'number',
    staleTime: 1000 * 60 * 5,
    retry: 2,
  });
};

export const useGetDeliveryAddressById = (addressId) => {
  return useQuery({
    queryKey: ['deliveryAddress', addressId],
    queryFn: async () => {
      const response = await DeliveryAddressService.getDeliveryAddressById(addressId);
      return response?.data?.address || response?.data || response?.address || response;
    },
    enabled: !!addressId,
    staleTime: 1000 * 60 * 5,
  });
};

export const useCreateDeliveryAddress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => DeliveryAddressService.createDeliveryAddress(payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['deliveryAddresses', variables?.customer_id],
      });
      queryClient.invalidateQueries({
        queryKey: ['defaultDeliveryAddress', variables?.customer_id],
      });
    },
  });
};

export const useUpdateDeliveryAddress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ addressId, payload }) =>
      DeliveryAddressService.updateDeliveryAddress({ addressId, payload }),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['deliveryAddresses', variables?.payload?.customer_id],
      });
      queryClient.invalidateQueries({
        queryKey: ['defaultDeliveryAddress', variables?.payload?.customer_id],
      });
      queryClient.invalidateQueries({ queryKey: ['deliveryAddress', variables?.addressId] });
    },
  });
};

export const useDeleteDeliveryAddress = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (addressId) => DeliveryAddressService.deleteDeliveryAddress(addressId),
    onSuccess: (data, addressId) => {
      queryClient.invalidateQueries({ queryKey: ['deliveryAddresses'] });
      queryClient.invalidateQueries({ queryKey: ['deliveryAddress', addressId] });
      queryClient.invalidateQueries({ queryKey: ['defaultDeliveryAddress'] });
    },
  });
};